'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getFullTimezoneOptions } from '@/config/timezones';
import type { TimezoneOption } from '@/config/timezones';

interface TimezoneContextValue {
  timezones: TimezoneOption[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const TimezoneContext = createContext<TimezoneContextValue | undefined>(undefined);

export const useTimezones = () => {
  const context = useContext(TimezoneContext);
  if (context === undefined) {
    throw new Error('useTimezones must be used within a TimezoneProvider');
  }
  return context;
};

interface TimezoneProviderProps {
  children: React.ReactNode;
}

export const TimezoneProvider: React.FC<TimezoneProviderProps> = ({ children }) => {
  const [timezones, setTimezones] = useState<TimezoneOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchTimezones = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getFullTimezoneOptions();
      setTimezones(data);
    } catch (err) {
      console.error('Failed to load timezones:', err);
      setError(err as Error);
      setTimezones([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTimezones();
  }, [fetchTimezones]);

  const value: TimezoneContextValue = {
    timezones,
    loading,
    error,
    refetch: fetchTimezones,
  };

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};
